import { useState, useMemo, useEffect } from "react";

import { useCountdownTimer } from "./useCountdownTimer";

import type {
  ProcessedEvent,
  MapTimeFilter,
  GeocodedEvent,
} from "@/types/calendar";
import {
  geocodeAddress,
  filterEventsByTimeRange,
  groupEventsByLocation,
  calculateMapBounds,
} from "@/utils/map.utils";
import {
  filterToNextOccurrences,
  enrichEventsWithNextOccurrences,
} from "@/utils/recurrence.utils";

interface UseMapViewReturn {
  filteredEvents: ProcessedEvent[];
  geocodedEvents: GeocodedEvent[];
  groupedEvents: Map<string, GeocodedEvent[]>;
  timeFilter: MapTimeFilter;
  setTimeFilter: (filter: MapTimeFilter) => void;
  selectedEvent: ProcessedEvent | null;
  setSelectedEvent: (event: ProcessedEvent | null) => void;
  isGeocoding: boolean;
  geocodingProgress: { current: number; total: number };
  mapBounds: any | null;
  eventsWithLocation: ProcessedEvent[];
}

/**
 * Hook for managing map view state and data
 * Following patterns from useCalendarEvents and useCalendarFilters
 */
export const useMapView = (events: ProcessedEvent[]): UseMapViewReturn => {
  const [timeFilter, setTimeFilter] = useState<MapTimeFilter>("week");
  const [selectedEvent, setSelectedEvent] = useState<ProcessedEvent | null>(
    null,
  );
  const [geocodedEvents, setGeocodedEvents] = useState<GeocodedEvent[]>([]);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodingProgress, setGeocodingProgress] = useState({
    current: 0,
    total: 0,
  });

  // Process recurring events to show only next occurrences
  const processedRecurringEvents = useMemo(() => {
    // First, enrich events with next occurrence data
    const enriched = enrichEventsWithNextOccurrences(events);

    // Then filter to show only next occurrences of recurring events
    return filterToNextOccurrences(enriched);
  }, [events]);

  // Filter events that have a location
  const eventsWithLocation = useMemo(() => {
    const filtered = processedRecurringEvents.filter((event) => {
      if (!event.location || event.location.trim() === "") return false;

      const nonAddressPatterns = [
        /^(zoom|teams|meet|skype|webex|gotomeeting|google meet)/i,
        /^(online|virtual|remote|digital|web|phone|call)/i,
        /^https?:\/\//,
        /^(tbd|tba|to be|n\/a)/i,
      ];

      // Check if it's explicitly not an address
      if (nonAddressPatterns.some((pattern) => pattern.test(event.location!))) {
        return false;
      }

      // Be more permissive - if it has a location that's not virtual, try to geocode it
      // This will include venue names, city names, or any text that might be geocodable
      return true;
    });

    return filtered;
  }, [processedRecurringEvents]);

  // Apply time filter
  const filteredEvents = useMemo(() => {
    return filterEventsByTimeRange(eventsWithLocation, timeFilter);
  }, [eventsWithLocation, timeFilter]);

  // Add countdown timers to geocoded events
  const { eventsWithCountdowns } = useCountdownTimer({
    events: geocodedEvents,
    enabled: true,
  });

  // Use events with countdowns for further processing
  const eventsForDisplay =
    eventsWithCountdowns.length > 0 ? eventsWithCountdowns : geocodedEvents;

  // Geocode filtered events sequentially with progress updates
  // Sequential processing prevents rate limiting from Nominatim
  useEffect(() => {
    const geocodeEvents = async () => {
      if (filteredEvents.length === 0) {
        setGeocodedEvents([]);
        setGeocodingProgress({ current: 0, total: 0 });
        return;
      }

      setIsGeocoding(true);
      setGeocodingProgress({ current: 0, total: filteredEvents.length });
      const geocodedResults: GeocodedEvent[] = [];

      console.log(
        `🗺️ Starting sequential geocoding of ${filteredEvents.length} events...`,
      );

      // Process events SEQUENTIALLY to avoid rate limiting
      // This is slower but much more reliable
      for (let i = 0; i < filteredEvents.length; i++) {
        const event = filteredEvents[i];
        console.log(
          `\n📌 Geocoding event ${i + 1}/${filteredEvents.length}: ${event.title}`,
        );

        try {
          const coords = await geocodeAddress(event.location!);
          const geocodedEvent = {
            ...event,
            coordinates: coords || undefined,
            geocodeError: coords ? undefined : "Could not locate address",
          } as GeocodedEvent;

          geocodedResults.push(geocodedEvent);

          // Update progress and state after each successful geocoding
          setGeocodingProgress({
            current: i + 1,
            total: filteredEvents.length,
          });
          setGeocodedEvents([...geocodedResults]);

          if (coords) {
            console.log(`✅ Successfully geocoded: ${event.location}`);
          } else {
            console.warn(`⚠️ Failed to geocode: ${event.location}`);
          }
        } catch (error) {
          console.error(`❌ Error geocoding event "${event.title}":`, error);

          // Add event with error
          geocodedResults.push({
            ...event,
            coordinates: undefined,
            geocodeError: "Geocoding error occurred",
          } as GeocodedEvent);

          setGeocodedEvents([...geocodedResults]);
        }
      }

      console.log(
        `\n✨ Geocoding complete! ${geocodedResults.filter((e) => e.coordinates).length}/${filteredEvents.length} successful`,
      );

      // Final update
      setGeocodingProgress({
        current: filteredEvents.length,
        total: filteredEvents.length,
      });
      setIsGeocoding(false);
    };

    geocodeEvents();
  }, [filteredEvents]);

  // Group events by location
  const groupedEvents = useMemo(() => {
    return groupEventsByLocation(eventsForDisplay);
  }, [eventsForDisplay]);

  // Calculate map bounds to fit all events
  const mapBounds = useMemo(() => {
    if (eventsForDisplay.length === 0) return null;
    return calculateMapBounds(eventsForDisplay);
  }, [eventsForDisplay]);

  // Reset selected event when filter changes
  useEffect(() => {
    setSelectedEvent(null);
  }, [timeFilter]);

  return {
    filteredEvents,
    geocodedEvents: eventsForDisplay, // Return events with countdowns
    groupedEvents,
    timeFilter,
    setTimeFilter,
    selectedEvent,
    setSelectedEvent,
    isGeocoding,
    geocodingProgress,
    mapBounds,
    eventsWithLocation,
  };
};
